import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { UserDto, CreateUserDto } from '../models';

interface ApiResponse<T> {
  message?: string;
  data?: T;
}

export interface UpdateUserProfileDto {
  firstName?: string;
  lastName?: string;
  phone?: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  getCurrentUser(): Observable<UserDto> {
    return this.http.get<UserDto>(`${this.apiUrl}/users/me`);
  }

  updateMyProfile(profile: UpdateUserProfileDto): Observable<UserDto> {
    return this.http
      .put<ApiResponse<UserDto> | UserDto>(`${this.apiUrl}/users/me/profile`, profile)
      .pipe(map((res) => ('data' in res && res.data ? res.data : (res as UserDto))));
  }

  register(createUserDto: CreateUserDto): Observable<UserDto> {
    return this.http.post<UserDto>(`${this.apiUrl}/users/register`, createUserDto);
  }

  /** Super admin only: list all users */
  getAllUsers(): Observable<UserDto[]> {
    return this.http.get<UserDto[]>(`${this.apiUrl}/users`);
  }

  /** Super admin or admin: update user role */
  updateUserRole(userId: string, role: string): Observable<UserDto> {
    return this.http.put<UserDto>(`${this.apiUrl}/users/${userId}/role`, { role });
  }

  /** Super admin only: block or unblock user */
  updateUserBlock(userId: string, blocked: boolean): Observable<UserDto> {
    return this.http.put<UserDto>(`${this.apiUrl}/users/${userId}/block`, { blocked });
  }

  /** Super admin only: send super admin invite to email */
  createSuperAdminInvite(email: string): Observable<{ token: string; expiresAt: string }> {
    return this.http.post<{ token: string; expiresAt: string }>(
      `${this.apiUrl}/super-admin-invites`,
      { email }
    );
  }

  /** Validate super admin invite token (public). */
  validateSuperAdminInvite(token: string): Observable<{ email: string; valid: boolean }> {
    return this.http.get<{ email: string; valid: boolean }>(
      `${this.apiUrl}/super-admin-invites/validate/${token}`
    );
  }

  /** Accept super admin invite (call after Firebase sign-up; pass the Firebase Id Token). */
  acceptSuperAdminInvite(
    body: {
      token: string;
      firebaseUid: string;
      email: string;
      profile: { firstName: string; lastName: string; phone?: string };
    },
    authToken: string
  ): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/super-admin-invites/accept`, body, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
  }

  createNotificationsTelegramLinkCode(): Observable<{ code: string; botUsername?: string }> {
    return this.http
      .post<
        ApiResponse<{ code: string; botUsername?: string }>
      >(`${this.apiUrl}/users/me/notifications-telegram/link-code`, {})
      .pipe(map((r) => r.data as { code: string; botUsername?: string }));
  }

  clearNotificationsTelegramLink(): Observable<void> {
    return this.http
      .delete<ApiResponse<void>>(`${this.apiUrl}/users/me/notifications-telegram`)
      .pipe(map(() => undefined));
  }
}
